import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import { categories, getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug) : [];
  const category = categories.find((c) => c.slug === article?.category);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-foreground-600 text-sm mb-6">抱歉，您要查看的文章不存在或已被删除</p>
            <Link
              to="/category/platform-intro"
              className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const pageTitle = `${article.title} - 成91平台`;

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://cheng91.com/article/${slug}`}
        ogTitle={pageTitle}
        ogDescription={article.excerpt}
        ogType="article"
      />

      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: category?.name || '分类', to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1.5 bg-accent-100 text-accent-700 rounded-lg text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {category?.name}
                </Link>
                <span className="text-foreground-500 text-sm">{article.date}</span>
                <span className="text-foreground-500 text-sm">{article.readTime}阅读</span>
                <span className="text-foreground-500 text-sm">{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-md text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom mb-14"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {related.length > 0 && (
            <section className="border-t border-background-200/70 pt-10 mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/article/${item.slug}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        title={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h4>
                      <span className="text-foreground-500 text-xs">{item.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}